import React, { useEffect, useState } from "react";

import { faLinkSimple } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonStandard, Text as Typography, toast } from "@performyard/py-components";

import { CopyToClipboard } from "../../../../../components/CopyToClipboard";
import Input from "../../../../../components/Inputs/InputText";
import ContentModal from "../../../../../components/Modal/Templates/ContentModal";
import FormModal from "../../../../../components/Modal/Templates/FormModal";
import Spacer from "../../../../../components/Spacer";
import Tooltip from "../../../../../components/Tooltip";

import { router } from "../../../../../app/router";

import { SURVEY_MODALS } from "../store/model";
import { useActions, useModal, useSetters, useSurveyId, useSurveyLink, useSurveyName } from "../store/store";

import {
	useCopySurvey,
	useCreateSurvey,
	useDeleteSurvey,
	useUpdateSurvey,
} from "../../../../../api/hooks/defs/surveys";

import { formatErrorMessage } from "../../../../../utils/TanstackQueryErrorHandling";

const CreateSurveyModal = ({ refetch }: { refetch: () => void }) => {
	const modal = useModal();
	const actions = useActions();
	const [name, setName] = useState<string>("");
	const [validationError, setValidationError] = useState<string>("");

	// create survey hook
	const onSuccess = async (data) => {
		actions.resetStore();
		refetch();
		router.navigate(`/surveys/${data._id}?tab=build`);
	};

	const onError = async (error, variables) => {
		if (
			error.data?.message.includes("Validation failed.") &&
			error.data?.details?.includes("String must contain at most 250 character(s)")
		) {
			setValidationError("Max character count is 250");
		} else {
			//default error handling is turned off for this mutation
			const messages = formatErrorMessage(error, variables);

			messages.forEach((message) => {
				toast.error(message);
			});
			actions.resetStore();
		}
	};
	const { mutate: createSurvey, isPending: isCreatingSurvey } = useCreateSurvey({ onSuccess, onError });

	// create survey modal
	return (
		<FormModal
			title={
				<Typography size="sub-heading" weight="semi-bold">
					Create New Survey
				</Typography>
			}
			isOpen={modal === SURVEY_MODALS.CREATE_SURVEY}
			buttons={[
				<div key="create-survey-cancel">
					<ButtonStandard
						type="button"
						size="large"
						onClick={() => {
							actions.resetStore();
							setName("");
							setValidationError("");
						}}
					>
						Cancel
					</ButtonStandard>
				</div>,
				<div key="create-survey-submit">
					<ButtonStandard type="submit" size="large" color="green" disabled={isCreatingSurvey}>
						Create New Survey
					</ButtonStandard>
				</div>,
			]}
			handleClose={() => {
				actions.resetStore();
				setName("");
				setValidationError("");
			}}
			onSubmit={(event) => {
				event.preventDefault();
				if (!name) {
					setValidationError("A survey name is required");
					return;
				}
				if (name.length > 250) {
					setValidationError("Max character count is 250");
					return;
				}
				createSurvey({ name });
			}}
		>
			<Input
				label="Survey Name"
				value={name}
				onChange={(value) => {
					setValidationError("");
					setName(value);
				}}
				placeholder="New Survey Name"
				errorMessage={validationError}
			/>
		</FormModal>
	);
};

const SurveyLinkModal = () => {
	const surveyLink = useSurveyLink();
	const modal = useModal();
	const actions = useActions();

	return (
		<ContentModal isOpen={modal === SURVEY_MODALS.SURVEY_LINK} handleClose={actions.resetStore}>
			<div className="flex w-full flex-col items-center justify-end gap-4">
				<Typography size="paragraph-1" color="neutral-9" weight="semi-bold">
					Survey Link
				</Typography>
				<div className="w-full text-center">
					<Input value={surveyLink} ariaLabel="Survey link" />
				</div>
				<Tooltip
					key="copy-link-button"
					message="Survey Link Copied"
					openOnClick
					hasArrow={false}
					turnOffAfter={2000}
					placement="left"
				>
					<CopyToClipboard text={surveyLink}>
						<div className="flex h-12 items-center">
							<ButtonStandard size="large" iconLeft={faLinkSimple}>
								Copy Link
							</ButtonStandard>
						</div>
					</CopyToClipboard>
				</Tooltip>
			</div>
		</ContentModal>
	);
};

const CloseOrDeleteSurveyModal = ({ refetch }: { refetch: () => void }) => {
	const modal = useModal();
	const setters = useSetters();
	const surveyId = useSurveyId();
	const surveyName = useSurveyName();

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [title, setTitle] = useState<string>("");
	const [buttonText, setButtonText] = useState<string>("");
	const [buttonColor, setButtonColor] = useState<"primary" | "green" | "red" | "neutral" | undefined>(undefined);
	const [keyPrefix, setKeyPrefix] = useState<string>("");
	const [bodyText, setBodyText] = useState<string>("");

	const onSuccessClose = async () => {
		setters.setModal(null);
		refetch();
	};
	const onSuccessDelete = async () => {
		setters.setModal(null);
		refetch();
	};
	const onError = async () => {
		setters.setModal(null);
	};

	const { mutate: deleteSurvey } = useDeleteSurvey({ onSuccess: onSuccessDelete, onError });
	const { mutate: closeSurvey } = useUpdateSurvey(surveyId, { onSuccess: onSuccessClose, onError });

	const isCloseSurvey = modal === SURVEY_MODALS.CLOSE_SURVEY;
	const isDeleteSurvey = modal === SURVEY_MODALS.DELETE_SURVEY;

	useEffect(() => {
		setIsOpen(isCloseSurvey || isDeleteSurvey);
	}, [isCloseSurvey, isDeleteSurvey]);

	useEffect(() => {
		switch (modal) {
			case SURVEY_MODALS.CLOSE_SURVEY:
				setTitle("Close Survey?");
				setButtonText("Yes, Close Survey");
				setButtonColor("green");
				setKeyPrefix("close");
				setBodyText("Your survey will no longer receive responses.");
				break;
			case SURVEY_MODALS.DELETE_SURVEY:
				setTitle("Delete Survey?");
				setButtonText("Yes, Delete Survey");
				setButtonColor("red");
				setKeyPrefix("delete");
				setBodyText("All results and data from this survey will be lost.");
				break;
			default:
				break;
		}
	}, [modal]);

	const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		if (isCloseSurvey) {
			closeSurvey({ status: "closed" });
		} else if (isDeleteSurvey) {
			deleteSurvey(surveyId);
		}
	};

	const handleClose = () => {
		setIsOpen(false);
		setters.setModal(null);
	};

	return (
		<ContentModal size="small" isOpen={isOpen} handleClose={handleClose}>
			<div role="dialog" className="flex w-full flex-col items-center">
				<Typography size="sub-heading" weight="semi-bold">
					{title}
				</Typography>
				<div className="min-w-0 max-w-[360px] pb-4">
					<div className="line-clamp-1">
						<Typography weight="regular" size="paragraph-1">
							{surveyName}
						</Typography>
					</div>
				</div>
				<Typography size="paragraph-1" weight="regular">
					<div className="text-center">{bodyText}</div>
				</Typography>
				<Spacer height={3} />
				<div className="flex justify-center gap-2 pb-4">
					<div key={`${keyPrefix}-survey-cancel`}>
						<ButtonStandard size="large" type="button" onClick={handleClose}>
							Cancel
						</ButtonStandard>
					</div>
					<div key={`${keyPrefix}-survey-submit`}>
						<ButtonStandard size="large" type="submit" color={buttonColor} onClick={handleSubmit}>
							{buttonText}
						</ButtonStandard>
					</div>
				</div>
			</div>
		</ContentModal>
	);
};

const SaveAsCopyModal = ({ refetch }: { refetch: () => void }) => {
	const [name, setName] = useState<string>("");
	const [validationError, setValidationError] = useState<string>("");

	const modal = useModal();
	const setters = useSetters();
	const surveyName = useSurveyName();
	const surveyId = useSurveyId();

	// copy survey hook
	const onSuccess = async (data) => {
		setters.setModal(null);
		refetch();
		router.navigate(`/surveys/${data._id}?tab=build`);
	};

	useEffect(() => {
		if (modal === SURVEY_MODALS.COPY_SURVEY) {
			setName(`${surveyName} (Copy)`);
			setValidationError("");
		} else {
			setName("");
			setValidationError("");
		}
		return () => {
			setName("");
			setValidationError("");
		};
	}, [surveyName, modal]);

	const onError = async (error, variables) => {
		if (
			error.data?.message.includes("Validation failed.") &&
			error.data?.details?.includes("String must contain at most 250 character(s)")
		) {
			setValidationError("Max character count is 250");
		} else {
			//default error handling is turned off for this mutation
			const messages = formatErrorMessage(error, variables);

			messages.forEach((message) => {
				toast.error(message);
			});
			setters.setModal(null);
		}
	};
	const { mutate: copySurvey, isPending: isCopyingSurvey } = useCopySurvey({ onSuccess, onError });

	const handleClose = () => {
		setters.setModal(null);
		setName("");
		setValidationError("");
	};

	// copy survey modal
	return (
		<FormModal
			title={
				<Typography size="sub-heading" weight="semi-bold">
					Save Survey as Copy
				</Typography>
			}
			isOpen={modal === SURVEY_MODALS.COPY_SURVEY}
			buttons={[
				<div key="copy-survey-cancel">
					<ButtonStandard type="button" size="large" onClick={handleClose}>
						Cancel
					</ButtonStandard>
				</div>,
				<div key="copy-survey-submit">
					<ButtonStandard
						data-testid="save-as-copy-button"
						type="submit"
						size="large"
						color="green"
						disabled={isCopyingSurvey}
					>
						Save as Copy
					</ButtonStandard>
				</div>,
			]}
			handleClose={handleClose}
			onSubmit={(event) => {
				event.preventDefault();
				if (!name) {
					setValidationError("A survey name is required");
					return;
				}
				if (name.length > 250) {
					setValidationError("Max character count is 250");
					return;
				}
				copySurvey({ name, surveyId });
			}}
		>
			<Input
				testId="save-as-copy-input"
				label="Survey Name"
				value={name}
				onChange={(value) => {
					setValidationError("");
					setName(value);
				}}
				placeholder="New Survey Name"
				errorMessage={validationError}
			/>
		</FormModal>
	);
};

/**
 * Renders all the surveys modals so we can switch open modal via the surevys store
 */
const SurveysModals = ({ refetch }: { refetch: () => void }) => {
	return (
		<>
			<CreateSurveyModal refetch={refetch} />
			<SurveyLinkModal />
			<CloseOrDeleteSurveyModal refetch={refetch} />
			<SaveAsCopyModal refetch={refetch} />
		</>
	);
};
export default SurveysModals;
